import { OFFERING_ROLES } from "@/lib/types/database/offerings";

/**
 * Normalization of the two `type: "json"` line-item shapes the offerings form
 * emits (`lineItemsType: "staff" | "hard-cost"`).
 *
 * Split out of `form-data-cleaner.ts` (module-size lane L17). Behaviour is
 * unchanged and pinned by `tests/unit/form-data-cleaner-branches.test.ts`.
 */

/** Raw staff line item as the form editor emits it, before normalization. */
interface RawStaffLineItem {
  role: string;
  hours: number;
  rate?: unknown;
}

/** Raw hard-cost line item as the form editor emits it, before normalization. */
interface RawHardCostLineItem {
  description: string;
  amount: number;
}

/** Normalized staff line item as the backend expects it (rate always resolved). */
export interface CleanStaffLineItem {
  role: string;
  hours: number;
  rate: number;
}

/** Normalized hard-cost line item as the backend expects it. */
export interface CleanHardCostLineItem {
  description: string;
  amount: number;
}

function isStaffLineItem(item: unknown): item is RawStaffLineItem {
  if (!item || typeof item !== "object") return false;
  const record = item as { role?: unknown; hours?: unknown };
  return Boolean(record.role) && typeof record.role === "string" && typeof record.hours === "number";
}

function isHardCostLineItem(item: unknown): item is RawHardCostLineItem {
  if (!item || typeof item !== "object") return false;
  const record = item as { description?: unknown; amount?: unknown };
  return (
    Boolean(record.description) &&
    typeof record.description === "string" &&
    typeof record.amount === "number"
  );
}

/**
 * Staff line items: drop malformed rows and ensure `{ role, hours, rate }` with
 * an EFFECTIVE rate — the row's own override when it is a finite number,
 * otherwise the role's `defaultRate` from `OFFERING_ROLES`, otherwise 0 (an
 * unknown role).
 */
export function cleanStaffLineItems(value: unknown): CleanStaffLineItem[] {
  if (!value || !Array.isArray(value) || value.length === 0) return [];
  return value.filter(isStaffLineItem).map((item) => {
    const roleDef = OFFERING_ROLES.find((r) => r.id === item.role);
    const effectiveRate =
      item.rate != null && !isNaN(Number(item.rate))
        ? Number(item.rate)
        : (roleDef?.defaultRate ?? 0);
    return {
      role: item.role,
      hours: Number(item.hours) || 0,
      rate: effectiveRate,
    };
  });
}

/** Hard-cost line items: drop malformed rows and ensure `{ description, amount }`. */
export function cleanHardCostLineItems(value: unknown): CleanHardCostLineItem[] {
  if (!value || !Array.isArray(value) || value.length === 0) return [];
  return value.filter(isHardCostLineItem).map((item) => ({
    description: item.description,
    amount: Number(item.amount) || 0,
  }));
}
